import {
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  useTheme
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { LoggerFactory } from "lego-gui-lib";
import { observer } from "mobx-react-lite";
import { runInAction } from "mobx";
import React, { ChangeEvent, ReactText, useContext } from "react";
import { useTranslation } from "react-i18next";
import { LocalOutput, treeBlockOutputsContext } from "../../schema";
import { TreeBlockType } from "../../TreeBlockType";
import { OutputLabelRenderer } from "../OutputLabel";

interface OutputListProps {
  outputOptions: LocalOutput[];
  treeBlockId: ReactText;
  treeBlockType: TreeBlockType;
  handleAddOutput: (blockId: ReactText, output: LocalOutput) => void;
  handleRemoveOutput: (blockId: ReactText, output: LocalOutput) => boolean;
}

export const OutputList = observer((props: OutputListProps) => {
  const {
    outputOptions,
    treeBlockId,
    treeBlockType,
    handleAddOutput,
    handleRemoveOutput
  } = props;
  const theme = useTheme();
  const i18n = useTranslation();
  const outputsContext = useContext(treeBlockOutputsContext);
  const log = LoggerFactory.create("OutputList");

  const handleToggle = (output: LocalOutput) => (): void => {
    const selectedOutputs = outputsContext.outputs[treeBlockId];
    const currentIndex = selectedOutputs.findIndex(
      (o: LocalOutput) => o.optionId == output.optionId
    );
    log.debug("handleToggle", output);
    const newChecked = [...selectedOutputs];
    if (currentIndex === -1) {
      newChecked.push(output);
      handleAddOutput(treeBlockId, output);
    } else {
      if (handleRemoveOutput(treeBlockId, output)) {
        newChecked.splice(currentIndex, 1);
      }
    }
    runInAction(() => {
      outputsContext.assign(treeBlockId, newChecked);
    });
  };

  const handleDeleteDynamicOption = (
    event:
      | React.MouseEvent<HTMLAnchorElement>
      | React.MouseEvent<HTMLButtonElement>,
    outputOption: LocalOutput
  ) => {
    outputsContext.deleteDynamicOutputOption(treeBlockId, outputOption);
  };

  return (
    <List
      dense={true}
      subheader={
        <ListSubheader
          style={{ backgroundColor: `${theme.palette.background.paper}` }}
        >
          {i18n.t("hns:tree:treeBlock:output:output")}
        </ListSubheader>
      }
    >
      {outputOptions && outputOptions.length ? (
        outputOptions.map((outputOption: LocalOutput, key: number) => (
          <OutputItem
            key={key}
            checked={
              outputsContext.outputs[treeBlockId]?.findIndex(
                (output: LocalOutput) =>
                  output.optionId == outputOption.optionId
              ) > -1
            }
            treeBlockType={treeBlockType}
            outputOption={outputOption}
            handleToggle={output => handleToggle(output)}
            handleDeleteOption={handleDeleteDynamicOption}
          />
        ))
      ) : (
        <ListItem>
          <Checkbox
            edge="start"
            disabled={true}
            indeterminate={true}
            tabIndex={-1}
            size={"small"}
            disableRipple
          />
          <ListItemText>N/A</ListItemText>
        </ListItem>
      )}
    </List>
  );
});

type OutputItemProps = {
  checked: boolean;
  outputOption: LocalOutput;
  treeBlockType: TreeBlockType;
  handleToggle: (
    outputOption: LocalOutput
  ) => (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  handleDeleteOption: (
    event:
      | React.MouseEvent<HTMLAnchorElement>
      | React.MouseEvent<HTMLButtonElement>,
    outputOption: LocalOutput
  ) => void;
};

const OutputItem = ({
  checked,
  outputOption,
  handleToggle,
  handleDeleteOption,
  treeBlockType
}: OutputItemProps) => {
  return (
    <ListItem>
      <ListItemIcon>
        <Checkbox
          checked={checked}
          edge="start"
          tabIndex={-1}
          disableRipple
          size={"small"}
          onChange={handleToggle(outputOption)}
          value={checked}
          disabled={outputOption.isAddedByDefault}
        />
      </ListItemIcon>
      <OutputLabelRenderer
        treeBlockType={treeBlockType}
        value={outputOption.value}
        label={outputOption.label}
        outputType={outputOption.type}
      />
      {outputOption.isDynamicOption && (
        <ListItemSecondaryAction>
          <IconButton
            aria-label="delete"
            color={"primary"}
            disabled={checked}
            edge="end"
            onClick={event => handleDeleteOption(event, outputOption)}
          >
            <DeleteIcon />
          </IconButton>
        </ListItemSecondaryAction>
      )}
    </ListItem>
  );
};
